import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  // Sayfa değiştiğinde mobil menüyü kapat
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Dil bazlı path'ler
  const getLocalizedPath = (page) => {
    const paths = {
      home: '/',
      gallery: language === 'de' ? '/galerie' : '/gallery',
      reservations: language === 'de' ? '/reservierungen' : '/reservations',
      contact: language === 'de' ? '/kontakt' : '/contact'
    };
    return paths[page];
  };

  const navLinks = [
    { page: 'home', path: getLocalizedPath('home'), label: t.nav.aboutUs },
    { page: 'gallery', path: getLocalizedPath('gallery'), label: t.nav.gallery },
    { page: 'reservations', path: getLocalizedPath('reservations'), label: t.nav.reservations },
    { page: 'contact', path: getLocalizedPath('contact'), label: t.nav.contact }
  ];

  // Aktif sayfa kontrolü (hem EN hem DE path'leri kontrol et)
  const isActive = (page) => {
    const currentPath = location.pathname;
    const pageRoutes = {
      home: ['/'],
      gallery: ['/gallery', '/galerie'],
      reservations: ['/reservations', '/reservierungen'],
      contact: ['/contact', '/kontakt']
    };
    return pageRoutes[page]?.includes(currentPath);
  };

  // Dil değiştiğinde URL'i güncelle
  const handleLanguageChange = (newLang) => {
    const currentPath = location.pathname;
    let newPath = currentPath;
    
    if (newLang === 'de') {
      if (currentPath === '/gallery') newPath = '/galerie';
      else if (currentPath === '/reservations') newPath = '/reservierungen';
      else if (currentPath === '/contact') newPath = '/kontakt';
      else if (currentPath === '/privacy') newPath = '/datenschutz';
      else if (currentPath === '/terms') newPath = '/agb';
      else if (currentPath === '/imprint') newPath = '/impressum';
    } else {
      if (currentPath === '/galerie') newPath = '/gallery';
      else if (currentPath === '/reservierungen') newPath = '/reservations';
      else if (currentPath === '/kontakt') newPath = '/contact';
      else if (currentPath === '/datenschutz') newPath = '/privacy';
      else if (currentPath === '/agb') newPath = '/terms';
      else if (currentPath === '/impressum') newPath = '/imprint';
    }
    
    setLanguage(newLang);
    if (newPath !== currentPath) {
      navigate(newPath);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#f8f6f3] border-b border-[#e8e4df]">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src="./images/slai-final-logo.png" 
              alt="SLÁI Events" 
              className="h-16 sm:h-20 object-contain mix-blend-multiply"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.page}
                to={link.path}
                className={`text-xs tracking-[0.2em] uppercase font-black transition-colors ${
                  isActive(link.page) ? 'text-[#2a2a2a]' : 'text-[#2a2a2a] hover:text-[#8b7355]'
                }`}
                style={{ fontFamily: "'Arial Black', 'Helvetica Bold', sans-serif" }}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleLanguageChange('de')}
                className={`flex items-center gap-1 px-3 py-2 border transition-all duration-300 ${
                  language === 'de' 
                    ? 'border-[#e67e22] bg-[#3a3a3a] text-white' 
                    : 'border-[#d4d0c8] text-[#6b6b6b] hover:border-[#e67e22] hover:bg-[#3a3a3a] hover:text-white'
                }`}
                aria-label="Deutsch"
              >
                <span className="text-lg">🇩🇪</span>
                <span className="text-xs font-medium">DE</span>
              </button>
              <button
                onClick={() => handleLanguageChange('en')}
                className={`flex items-center gap-1 px-3 py-2 border transition-all duration-300 ${
                  language === 'en' 
                    ? 'border-[#e67e22] bg-[#3a3a3a] text-white' 
                    : 'border-[#d4d0c8] text-[#6b6b6b] hover:border-[#e67e22] hover:bg-[#3a3a3a] hover:text-white'
                }`}
                aria-label="English"
              >
                <span className="text-lg">🇺🇸</span>
                <span className="text-xs font-medium">EN</span>
              </button>
            </div>
          </div>

          {/* Mobile: Language Buttons + Hamburger Menu */}
          <div className="md:hidden flex items-center gap-2">
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleLanguageChange('de')}
                className={`flex items-center px-2 py-1 border transition-all ${
                  language === 'de' 
                    ? 'border-[#e67e22] bg-[#3a3a3a] text-white' 
                    : 'border-[#d4d0c8] text-[#6b6b6b]'
                }`}
                aria-label="Deutsch"
              >
                <span className="text-lg">🇩🇪</span>
              </button>
              <button
                onClick={() => handleLanguageChange('en')}
                className={`flex items-center px-2 py-1 border transition-all ${
                  language === 'en' 
                    ? 'border-[#e67e22] bg-[#3a3a3a] text-white' 
                    : 'border-[#d4d0c8] text-[#6b6b6b]'
                }`}
                aria-label="English"
              >
                <span className="text-lg">🇺🇸</span>
              </button>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-[#1a1a1a] hover:bg-[#e8e4df]"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Hamburger Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-[#e8e4df] animate-in slide-in-from-top bg-[#f8f6f3]">
            {navLinks.map((link) => (
              <Link
                key={link.page}
                to={link.path}
                className={`block px-4 py-3 text-xs tracking-[0.2em] uppercase font-black transition-colors ${
                  isActive(link.page)
                    ? 'bg-[#3a3a3a] text-white'
                    : 'text-[#2a2a2a] hover:bg-[#e8e4df] hover:text-[#8b7355]'
                }`}
                style={{ fontFamily: "'Arial Black', 'Helvetica Bold', sans-serif" }}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;